<template>
    <div class="global-banners">
        <!-- Banner when receive a new announcement -->
        <div v-if="showAnnouncementBanner" class="announcements-container">
            <banner
                v-for="announcement in nonDismissedAnnouncements"
                :key="announcement.id"
                class="q-ma-md shadow-5"
                :hasAction="true"
                :showDismiss="true"
                actionLabel="Dismiss"
                :title="announcement.title"
                :message="announcement.message"
                :type="getBannerType(announcement.type)"
                :customIcon="getBannerIcon(announcement.type)"
                @action="dismissBanner(announcement.id)"
            />
        </div>

        <!-- Banner when the device clock is out of sync -->
        <announcement-banner
            v-if="isClockOutOfSync"
            icon="fas fa-alarm-clock"
            :message="
                $t(
                    'Your device clock is out of sync. This can cause data integrity issues in the application. Please update your time settings on your computer or device.'
                )
            "
            :actionLabel="$t('Check')"
            :action="loadClientSettings"
            :showDismiss="false"
            level="warning"
        />
        <!-- Banner when user is not connected to the internet -->
        <announcement-banner
            v-if="internetBanner.isVisible"
            :icon="internetBanner.icon"
            :message="internetBanner.message"
            @dismiss="internetBanner.isVisible = false"
        />
        <!-- Banner when SignalR connection fails -->
        <announcement-banner
            v-if="showSignalRConnectionFailedBanner"
            :icon="signalRConnectionFailedBanner.icon"
            :message="signalRConnectionFailedBanner.message"
            :action="signalRConnectionFailedBanner.reconnectFunction"
            :showDismiss="false"
        />
        <!-- Banner when subscribing to SignalR group fails  -->
        <announcement-banner
            v-if="showSubscribeBanner && !showSignalRConnectionFailedBanner"
            :icon="subscribeBanner.icon"
            :message="subscribeBanner.message"
            :action="subscribeBanner.reconnectFunction"
            @dismiss="dismissSubscribeBannerAction()"
        />
        <!-- Banner when SignalR is reconnecting -->
        <announcement-banner
            v-if="showReconnectingBanner"
            :icon="reconnectingBanner.icon"
            :message="reconnectingBanner.message"
            @dismiss="dismissReconnectingBannerAction()"
        />
        <announcement-banner
            v-if="showSensorDisabledBanner"
            :message="sensorDisabledMessage"
            icon="fa-solid fa-circle-exclamation"
            @dismiss="dismissSensorDisabledBannerAction()"
        />
        <announcement-banner
            v-if="showInitializeResourcesErrorBanner"
            :message="initializeResourcesErrorBanner.message"
            icon="fa-solid fa-circle-exclamation"
            :showDismiss="false"
            :action="initializeResourcesErrorBanner.reconnectFunction"
            :actionLabel="$t('Retry')"
        />
        <!-- Banner when no shifts are defined for facility -->
        <announcement-banner
            v-if="noActiveShiftBanner.show"
            :message="noActiveShiftBanner.message"
            :icon="noActiveShiftBanner.icon"
            level="warning"
            :showDismiss="false"
            :action="noActiveShiftBanner.action"
            :actionLabel="noActiveShiftBanner.actionLabel"
            data-testid="no-active-shift-banner"
        />
        <!-- Banner when push notification registration fails -->
        <announcement-banner
            v-if="isCapacitor && showPushNotificationErrorBanner"
            :icon="pushNotificationErrorBanner.icon"
            level="warning"
            :message="pushNotificationErrorBanner.message"
            @dismiss="dismissPushNotificationErrorBannerAction()"
            :action="pushNotificationErrorBanner.reconnectFunction"
            :actionLabel="$t('Retry')"
        />
    </div>
</template>

<script>
import AnnouncementBanner from '@components/AnnouncementBanner.vue';
import Banner from '@components/Banner.vue';

import { mapState, mapActions } from 'pinia';
import { useBannersStore } from '@stores/banners.js';
import { useSharedStore } from '@stores/shared.js';
import { useSettingsStore } from '@stores/settings.js';
import { useAuthStore } from '@stores/auth';

const BANNER_DETAILS = {
    info: {
        icon: 'fas fa-info-circle',
        level: 'info'
    },
    warning: {
        icon: 'fas fa-exclamation-triangle',
        level: 'warning'
    },
    unplanned: {
        icon: 'fa-solid fa-screwdriver-wrench',
        level: 'error'
    },
    planned: {
        icon: 'fa-solid fa-screwdriver-wrench',
        level: 'info'
    }
};

export default {
    name: 'GlobalBanners',
    components: {
        AnnouncementBanner,
        Banner
    },
    created() {
        this.initInternetConnWatcher();
    },
    props: {
        signalRConnectionFailedBanner: {
            type: Object,
            required: true
        },
        subscribeBanner: {
            type: Object,
            required: true
        },
        reconnectingBanner: {
            type: Object,
            required: true
        },
        initializeResourcesErrorBanner: {
            type: Object,
            required: true
        },
        noActiveShiftBanner: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            // browser navigator object - don't set as null keys are added later to make it reactive (also don't set as navigator object itself, Vue does not allow to add props using this.$set for reactivity)
            navigator: {}
        };
    },
    watch: {
        /**
         * Handles global connection banner visibility.
         */
        'navigator.onLine': {
            handler(isConnected) {
                this.internetBanner.isVisible = !isConnected;
            }
        }
    },
    computed: {
        ...mapState(useSettingsStore, [
            'clockDriftMs',
            'isTrackingQualityEvents',
            'getNonDismissedAnnouncements'
        ]),
        ...mapState(useBannersStore, [
            'showSignalRConnectionFailedBanner',
            'showPushNotificationErrorBanner',
            'showSensorDisabledBanner',
            'showSubscribeBanner',
            'showReconnectingBanner',
            'showInitializeResourcesErrorBanner'
        ]),
        ...mapState(useAuthStore, {
            getUser: 'user'
        }),
        /**
         * Check if mobile build
         * @returns boolean
         */
        isCapacitor() {
            return this.$q.platform.is.capacitor;
        },
        /**
         * Get non-dismissed announcements.
         * @return {Object[]} - List of non-dismissed announcements.
         */
        nonDismissedAnnouncements() {
            return this.getNonDismissedAnnouncements;
        },
        /**
         * Control the visibility of the announcement banner.
         * @return {Boolean}
         */
        showAnnouncementBanner() {
            return this.nonDismissedAnnouncements.length > 0;
        },
        /**
         * Check if the clock is out of sync. We consider that it is out of sync
         * if the drift is greater than 0 seconds.
         * @return {Boolean}
         */
        isClockOutOfSync() {
            return Math.abs(this.clockDriftMs) > 0;
        },
        sensorDisabledMessage() {
            return this.$t(
                'Some sensors connected to this production line are disabled. Contact support to re-enable them'
            );
        },
        internetBanner() {
            return {
                isVisible: !this.navigator.onLine,
                icon: 'fas fa-wifi-slash',
                message: this.$t(
                    'You have lost connection to the internet. This application is offline.'
                )
            };
        },
        pushNotificationErrorBanner() {
            return {
                icon: 'fas fa-bell-slash',
                isLoading: false,
                reconnectFunction: async () => {
                    await this.managePushNotifications();
                },
                message: this.$t(
                    'An error occurred while registering this device for push notifications. Please try again.'
                )
            };
        }
    },

    methods: {
        ...mapActions(useBannersStore, [
            'dismissPushNotificationErrorBannerAction',
            'dismissSensorDisabledBannerAction',
            'dismissSubscribeBannerAction',
            'dismissReconnectingBannerAction',
            'dismissInitializeResourcesErrorBannerAction'
        ]),
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useSettingsStore, [
            'dismissAnnouncement',
            'loadSettings'
        ]),
        ...mapActions(useAuthStore, ['isAuthenticatedAction']),
        /**
         * Dismiss the announcement banner.
         * @param {String} announcementId - Announcement ID.
         */
        dismissBanner(announcementId) {
            this.dismissAnnouncement(announcementId);
        },
        /**
         * Get banner type based on announcement type.
         * @param {String} announcementType - Announcement type.
         * @return {String} - Banner type.
         */
        getBannerType(announcementType) {
            return BANNER_DETAILS[announcementType].level;
        },
        /**
         * Get banner icon based on announcement type.
         * @param {String} announcementType - Announcement type.
         * @return {String} - Banner icon.
         */
        getBannerIcon(announcementType) {
            return BANNER_DETAILS[announcementType].icon;
        },
        /**
         * Store client settings in state.
         */
        async loadClientSettings() {
            try {
                await this.loadSettings();
            } catch (error) {
                console.error('Failed to load client settings: ', error);
                this.$router.push({ name: 'error' });
            }
        },
        /**
         * Initialize watcher that checks if client has connection to internet.
         */
        initInternetConnWatcher() {
            // register navigator object to Vue to react to connection changes
            this.navigator['onLine'] = navigator.onLine;
            window.addEventListener('online', () => {
                this.navigator['onLine'] = navigator.onLine;
            });
            window.addEventListener('offline', () => {
                this.navigator['onLine'] = navigator.onLine;
            });
        },
        /**
         * Register / unregister the device for push notifications when user is logged in/out.
         * Only applicable for Capacitor platform.
         */
        async managePushNotifications() {
            if (this.$q.platform.is.capacitor) {
                const isAuthenticated = await this.isAuthenticatedAction();
                if (isAuthenticated) {
                    await this.$pushNotificationManager.registerDevice();
                } else {
                    await this.$pushNotificationManager.unregisterDevice();
                }
            }
        }
    }
};
</script>

<style lang="scss">
// The announcement banner container will be fixed at the top of the page.
// this will ensure that the banner is always visible to the user,
// and it will not be affected by other elements on the page.
.announcements-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 5000; // above the main content and below the notification dialog
}
</style>
